<template>
  <div v-if="showModal" class="modal-container">
    <div
      class="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-40"
    ></div>
    <div class="z-50 w-[550px] max-w- rounded-[12px]">
      <div class="flex justify-end">
        <button
          @click="$emit('close')"
          class="px-1 py-1 bg-primaryLight rounded-full mb-8 hover:bg-gray-300"
        >
          <img
            src="@/assets/tasks/iconoir_cancel.svg"
            alt="Close"
            class="h-6"
          />
        </button>
      </div>

      <div
        class="modal-content relative bg-white max--lg w-full max-h-[90vh] m-auto flex-col flex rounded-[12px] shadow-lg"
      >
        <div class="w-full border-b-[1px] mb-4 px-6 py-4">
          <h2 class="text-[18px] text-grayDark font-bold">
            {{ modalTitle }}
          </h2>
        </div>

        <form @submit.prevent="handleSubmit" class="overflow-y-auto px-8">
          <div class="space-y-4 mb-4">
            <!-- Team Name (only for Create Team mode) -->
            <div v-if="mode === 'createTeam'" class="flex flex-col gap-2">
              <label class="flex justify-between text-[13px] font-medium text-gray-700">
                Team Name
								<span class="text-[#DB1B24] italic">*Required</span>
              </label>
              <input
                v-model="teamName"
                type="text"
                placeholder="Enter team name"
                class="mt-1 block w-full px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-[#F6F8F9]"
                required
              />
            </div>

            <!-- Project Selection -->
            <div class="flex flex-col gap-2">
              <label class="block text-[13px] font-medium text-gray-700">
                Select a project
              </label>
              <CustomSelect
                :options="displayProjects"
                v-model="selectedProject"
                labelKey="project_name"
                valueKey="project_id"
                placeholder="Search and select a Project"
                @change="onProjectChange"
              />
              <div v-if="projectLoading" class="text-blue-500 text-sm mt-1">
                Loading projects...
              </div>
              <div v-if="projectError" class="text-red-500 text-sm mt-1">
                {{ projectError }}
              </div>
            </div>

            <!-- Team Selection (only for Add Talent mode) -->
            <div v-if="mode === 'addTalent'" class="flex flex-col gap-2">
              <label class="block text-[13px] font-medium text-gray-700">
                Select a Team
              </label>
              <CustomSelect
                :options="teams"
                v-model="selectedTeam"
                labelKey="team_name"
                valueKey="team_id"
                placeholder="Select a team"
                :disabled="!selectedProject || teamsLoading"
              />
              <div v-if="teamsLoading" class="text-blue-500 text-sm mt-1">
                Loading teams...
              </div>
              <div v-if="teamError" class="text-red-500 text-sm mt-1">
                {{ teamError }}
              </div>
            </div>

            <!-- Talent Selection -->
            <div class="flex flex-col gap-2">
              <label class="block text-[13px] font-medium text-gray-700">
                {{
                  mode === "createTeam"
                    ? "Add Talents to Team"
                    : "Select Talent"
                }}
              </label>
              <CustomSelect
                :options="formattedTalents"
                v-model="selectedTalents"
                labelKey="displayName"
                valueKey="talent_id"
                placeholder="Search and select Talents by email"
                multiple
                :disabled="
                  !selectedProject ||
                  talentsLoading ||
                  (mode === 'addTalent' && !selectedTeam)
                "
              />
              <div v-if="talentsLoading" class="text-blue-500 text-sm mt-1">
                Loading talents...
              </div>
              <div v-if="talentError" class="text-red-500 text-sm mt-1">
                {{ talentError }}
              </div>
            </div>

            <!-- CSV Upload for Talents (Optional) -->
            <div v-if="mode === 'createTeam'" class="flex flex-col gap-2">
              <label class="block text-[13px] font-medium text-gray-700">
                Add Members via CSV (Optional)
              </label>
              <input
                type="file"
                accept=".csv"
                @change="handleFileUpload"
                class="mt-1 block w-full px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-[#F6F8F9]"
              />
              <div v-if="memberEmails.length > 0" class="text-sm text-green-600 mt-1">
                Found {{ memberEmails.length }} valid emails.
              </div>
            </div>

            <!-- Description (optional for Create Team mode) -->
            <div v-if="mode === 'createTeam'" class="flex flex-col gap-2">
              <label class="block text-[13px] font-medium text-gray-700">
                Description (Optional)
              </label>
              <textarea
                v-model="description"
                placeholder="Enter team description"
                rows="3"
                class="mt-1 block w-full px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-[#F6F8F9]"
              ></textarea>
            </div>
          </div>

          <div class="flex mb-4 text-[14px]">
            <button
              type="submit"
              class="gap-2 py-2 px-4 bg-primaryDarkBg w-auto min-w-full h-[42px] text-white rounded"
              :disabled="submitDisabled"
            >
              {{ submitButtonText }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";
import CustomSelect from "../team/CustomSelect.vue";

export default {
  name: "UnifiedTeamModal",
  components: {
    CustomSelect,
  },
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => ["createTeam", "addTalent"].includes(value),
    },
    projects: {
      type: Array,
      default: () => [],
    },
    // Props for pre-selected values (useful for Add Talent mode)
    preSelectedProjectId: {
      type: [String, Number],
      default: null,
    },
    preSelectedTeamId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      selectedProject: null,
      selectedTeam: null,
      teamName: "",
      // description: "",
      selectedTalents: [],
      teams: [],
      talents: [],
      projectLoading: false,
      teamsLoading: false,
      talentsLoading: false,
      projectError: null,
      teamError: null,
      talentError: null,
      localProjects: [],
      memberEmails: [],
      isSubmitting: false,
    };
  },
  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    },
    modalTitle() {
      return this.mode === "createTeam"
        ? "Create a New Team"
        : "Add Talent to Team";
    },
    submitButtonText() {
      if (this.isSubmitting) {
        return this.mode === "createTeam" ? "Creating Team..." : "Saving...";
      }
      return this.mode === "createTeam" ? "Create Team" : "Save";
    },
    submitDisabled() {
      if (this.isSubmitting) return true;
      const hasTalents = this.selectedTalents.length > 0 || this.memberEmails.length > 0;
      const baseDisabled =
        !this.selectedProject ||
        !hasTalents ||
        this.projectLoading ||
        this.teamsLoading ||
        this.talentsLoading;

      if (this.mode === "createTeam") {
        return baseDisabled || !this.teamName.trim();
      } else {
        return baseDisabled || !this.selectedTeam;
      }
    },
    formattedTalents() {
      return this.talents.map((talent) => {
        const email = talent.talent_email || "";
        const firstName = talent.talent_first_name || email;
        const lastName = talent.talent_last_name || "";
        const displayName = `${firstName} ${lastName}`;

        return {
          ...talent,
          displayName,
          email,
        };
      });
    },
    displayProjects() {
      return this.projects.length > 0 ? this.projects : this.localProjects;
    },
  },
  watch: {
    showModal(newValue) {
      if (newValue) {
        this.resetForm();
        this.loadInitialData();
      }
    },
    mode() {
      this.resetForm();
    },
  },
  methods: {
    handleSubmit() {
      if (this.mode === "createTeam") {
        this.submitCreateTeam();
      } else {
        this.submitAddTalent();
      }
    },

    async submitCreateTeam() {
      try {
        this.isSubmitting = true;
        if (!this.selectedProject || !this.teamName.trim()) {
          this.$toast.error('Please fill all required fields');
          this.isSubmitting = false;
          return;
        }

        const teamData = {
          project_id: this.selectedProject,
          name: this.teamName,
          description: this.description,
          members: [],
          member_emails: this.memberEmails,
        };

        const response = await apiClient.post("/web/teams/", teamData);
        console.log("Team created:", response.data);

        // If talents are selected, add them to the team
        if (this.selectedTalents.length > 0) {
          await this.addTalentsToTeam(response.data.data.team_id);
        }

        this.$toast.success("Team created successfully!");

        this.$emit("team-created", response.data.data);

        setTimeout(() => {
          this.$emit("close");
        }, 1500);
      } catch (error) {
        this.isSubmitting = false;
        console.error("Error creating team:", error);
        this.$toast.error(error.response?.data?.message || "An error occurred");
      }
    },

    async submitAddTalent() {
      if (
        !this.selectedProject ||
        !this.selectedTeam ||
        this.selectedTalents.length === 0
      ) {
        this.$toast.error('Please fill all required fields');
        return;
      }

      try {
        this.isSubmitting = true;
        await this.addTalentsToTeam(this.selectedTeam);

        this.$toast.success(`${this.selectedTalents.length} talent(s) successfully added to the team!`);

        this.$emit("talents-added", {
          teamId: this.selectedTeam,
          projectId: this.selectedProject,
          talent_ids: this.selectedTalents,
        });

        setTimeout(() => {
          this.$emit("close");
        }, 1500);
      } catch (error) {
        this.isSubmitting = false;
        console.error("Error adding talents:", error);
        this.$toast.error(error.response?.data?.message || "Failed to add talents to team");
      }
    },

    async addTalentsToTeam(teamId) {
      const results = {
        succeeded: [],
        failed: [],
        errorMessages: [],
      };

      for (const talentId of this.selectedTalents) {
        try {
          const talent = this.talents.find((t) => t.talent_id === talentId);

          if (!talent) {
            console.error(`Could not find talent with ID ${talentId}`);
            results.failed.push(talentId);
            continue;
          }

          const projectMemberId = talent.project_member_id;

          if (!projectMemberId) {
            console.error(`No project_member_id found for talent ${talentId}`);
            results.failed.push(talentId);
            continue;
          }

          const talentData = {
            project_member_id: projectMemberId,
          };

          await apiClient.post(`/web/teams/${teamId}/members`, talentData);
          results.succeeded.push(talentId);
        } catch (error) {
          console.error(`Error adding talent ID ${talentId}:`, error);
          results.failed.push(talentId);

          const errorMessage =
            error.response?.data?.message || error.message || "Unknown error";
          if (!results.errorMessages.includes(errorMessage)) {
            results.errorMessages.push(errorMessage);
          }
        }
      }

      if (results.failed.length > 0) {
        const errorMessage =
          results.errorMessages.length > 0
            ? results.errorMessages.join("; ")
            : "Failed to add some team members";
        throw new Error(errorMessage);
      }
    },

    async onProjectChange() {
      if (this.selectedProject) {
        this.selectedTeam = null;
        this.selectedTalents = [];

        if (this.mode === "addTalent") {
          await this.fetchTeams(this.selectedProject);
        }

        await this.fetchProjectMembers(this.selectedProject);
      } else {
        this.teams = [];
        this.talents = [];
      }
    },

    async fetchTeams(projectId) {
      try {
        this.teamsLoading = true;
        this.teamError = null;
        console.log(`Fetching teams for project ${projectId}...`);

        const response = await apiClient.get(`/web/teams/project/${projectId}`);

        if (response.data && response.data.data) {
          this.teams = response.data.data;
          console.log(`Loaded ${this.teams.length} teams:`, this.teams);

          // If we have a pre-selected team ID, select it now
          if (
            this.preSelectedTeamId &&
            this.teams.some((team) => team.team_id == this.preSelectedTeamId)
          ) {
            this.selectedTeam = this.preSelectedTeamId;
          }
        } else {
          console.error("Unexpected response format:", response);
          this.teamError = "Received invalid data format from server";
          this.teams = [];
        }
      } catch (error) {
        console.error("Error fetching teams:", error);
        this.teamError =
          error.response?.data?.message || "Failed to load teams";
        this.teams = [];
      } finally {
        this.teamsLoading = false;
      }
    },

    async fetchProjectMembers(projectId) {
      if (!projectId) {
        console.log("No project ID provided for fetching members");
        return;
      }

      try {
        this.talentsLoading = true;
        this.talentError = null;
        console.log(`Fetching project members for project ${projectId}...`);

        const response = await apiClient.get(
          `/web/projects/${projectId}/project_members/`
        );

        if (response.data && response.data.data) {
          this.talents = response.data.data;
          console.log(`Loaded ${this.talents.length} talents:`, this.talents);
        } else {
          console.error("Unexpected response format:", response);
          this.talentError = "Received invalid data format from server";
          this.talents = [];
        }
      } catch (error) {
        console.error("Error fetching project members:", error);
        this.talentError =
          error.response?.data?.message || "Failed to load project members";
        this.talents = [];
      } finally {
        this.talentsLoading = false;
      }
    },

    async loadInitialData() {
      // Apply pre-selected project if provided
      if (this.preSelectedProjectId) {
        this.selectedProject = this.preSelectedProjectId;

        if (this.mode === "addTalent") {
          await this.fetchTeams(this.preSelectedProjectId);
        }

        await this.fetchProjectMembers(this.preSelectedProjectId);
      }

      // Load projects if not provided as props
      if (!this.projects || this.projects.length === 0) {
        await this.fetchProjects();
      }
    },

    async fetchProjects() {
      try {
        this.projectLoading = true;
        this.projectError = null;

        let response;
        if (this.isManager) {
          response = await apiClient.get(`/web/manager/me`);
          const managerData = response.data.data;
          this.localProjects = managerData.projects || [];
        } else if (this.isAdmin) {
          response = await apiClient.get("/web/projects_all");
          this.localProjects = response.data.data || [];
        } else if (this.isTalent) {
          response = await apiClient.get("/web/talent/me");
          const talentData = response.data.data;
          this.localProjects = talentData.projects || [];
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
        this.projectError = "Failed to load projects. Please try again.";
      } finally {
        this.projectLoading = false;
      }
    },

    resetForm() {
      this.selectedProject = this.preSelectedProjectId || null;
      this.selectedTeam = this.preSelectedTeamId || null;
      this.teamName = "";
      this.description = "";
      this.selectedTalents = [];
      this.memberEmails = [];
      this.isSubmitting = false;

      // Don't reset teams and talents arrays here as they might be needed
      // They will be reset/loaded in onProjectChange if needed
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi;
        const matches = text.match(emailRegex) || [];
        this.memberEmails = [...new Set(matches)];
      };
      reader.readAsText(file);
    },
  },
};
</script>

<style scoped>
.modal-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  inset: 0;
  z-index: 50;
}

.modal-content {
  position: relative;
  z-index: 60;
}
</style>
